import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  Download,
  AccountBalance,
  Receipt,
  BarChart as BarChartIcon,
  InsertDriveFile,
  PictureAsPdf,
  Person,
  Visibility,
  Print,
  Refresh,
  FilterList,
  Search,
  Close,
  CheckCircle,
  Warning,
  Error,
  Info,
  Star,
  TrendingDown,
  TrendingFlat,
  AccessTime,
  Euro,
  People,
  ShoppingCart,
  AttachMoney,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import AdminDailyClosingReportsService, { 
  AdminDailyClosingReport, 
  AdminInterimSalesSummary, 
  StaffPerformanceSummary,
} from '../../services/adminDailyClosingReportsService';
import { LogoService } from '../../services/logoService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface DailyClosingResetEvent {
  id: string;
  report_id: string;
  report_date: string;
  cashier_name: string;
  branch: string;
  previous_status: string;
  performed_by_name: string;
  performed_by_email?: string;
  reason: string;
  created_at: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDailyClosingReportsPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State management
  const [dailyReports, setDailyReports] = useState<AdminDailyClosingReport[]>([]);
  const [interimSummaries, setInterimSummaries] = useState<AdminInterimSalesSummary[]>([]);
  const [staffPerformance, setStaffPerformance] = useState<StaffPerformanceSummary[]>([]);
  const [resetEvents, setResetEvents] = useState<DailyClosingResetEvent[]>([]);
  
  // Dialog states
  const [generateReportDialog, setGenerateReportDialog] = useState(false);
  const [viewReportDialog, setViewReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AdminDailyClosingReport | null>(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<StaffPerformanceSummary | null>(null);
  
  // Form states
  const [reportForm, setReportForm] = useState({
    staffId: '',
    reportDate: new Date().toISOString().split('T')[0],
    openingCash: 0,
    actualCash: 0,
  });

  const reportsService = AdminDailyClosingReportsService.getInstance();

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 [ADMIN DAILY CLOSING] Loading admin daily closing reports...');
      
      const results = await Promise.allSettled([
        reportsService.getAllStaffDailyClosingReports(dateFilter, staffFilter),
        reportsService.getStaffPerformanceSummary(),
        reportsService.getAllInterimSalesSummaries(),
        reportsService.getRecentResetEvents(),
      ]);

      const reports = results[0].status === 'fulfilled' ? results[0].value : [];
      const performance = results[1].status === 'fulfilled' ? results[1].value : [];
      const summaries = results[2].status === 'fulfilled' ? results[2].value : [];
      const resets = results[3].status === 'fulfilled' ? results[3].value : [];

      setDailyReports(reports);
      setStaffPerformance(performance);
      setInterimSummaries(summaries);
      setResetEvents(resets);

      // Log any individual failures
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const names = ['daily reports', 'staff performance', 'interim summaries', 'reset events'];
          console.warn(`⚠️ [ADMIN DAILY CLOSING] Failed to load ${names[index]}:`, result.reason);
        }
      });

      console.log(`✅ [ADMIN DAILY CLOSING] Loaded ${reports.length} daily closing reports, ${performance.length} staff performance summaries, ${summaries.length} interim summaries, and ${resets.length} reset events`);
      
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to load data:', error);
      setError('Failed to load admin daily closing reports');
    } finally {
      setLoading(false);
    }
  };

  const generateStaffReport = async () => {
    try {
      setLoading(true);
      
      console.log('📊 [ADMIN DAILY CLOSING] Generating staff daily closing report...');
      
      const report = await reportsService.generateStaffDailyClosingReport(
        reportForm.staffId,
        reportForm.reportDate,
        reportForm.openingCash,
        reportForm.actualCash
      );
      
      setDailyReports(prev => [report, ...prev]);
      setGenerateReportDialog(false);
      
      console.log('✅ [ADMIN DAILY CLOSING] Staff daily closing report generated successfully');
      
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to generate staff daily closing report:', error);
      setError('Failed to generate staff daily closing report');
    } finally {
      setLoading(false);
    }
  };

  const generateInterimSummary = async (staffId: string) => {
    try {
      console.log('📊 [ADMIN INTERIM SALES] Generating interim sales summary for staff...');
      
      const summary = await reportsService.generateStaffInterimSalesSummary(staffId);
      
      setInterimSummaries(prev => [summary, ...prev]);
      
      console.log('✅ [ADMIN INTERIM SALES] Interim sales summary generated successfully');
      
    } catch (error) {
      console.error('❌ [ADMIN INTERIM SALES] Failed to generate interim sales summary:', error);
      setError('Failed to generate interim sales summary');
    }
  };

  const exportReportToPDF = async (report: AdminDailyClosingReport) => {
    try {
      console.log('📄 [EXPORT] Exporting admin daily closing report to PDF...');
      
      // Import jsPDF
      const { jsPDF } = await import('jspdf');
      const { autoTable } = await import('jspdf-autotable');
      
      const doc = new jsPDF();
      
      // Add app logo and header (German Standards)
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('SAHEEH SUSHI & BOWLS', 105, 20, { align: 'center' });
      
      // Add actual app logo using LogoService
      await LogoService.addLogoToPDF(doc, 85, 25, 40, 20);
      
      // Add decorative line
      doc.setLineWidth(0.5);
      doc.line(20, 40, 190, 40);
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('MITARBEITER TAGESABSCHLUSSBERICHT', 105, 45, { align: 'center' });
      doc.text('(Staff Daily Closing Report)', 105, 52, { align: 'center' });
      
      // Staff Information
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Datum: ${new Date(report.report_date).toLocaleDateString('de-DE')}`, 20, 65);
      doc.text(`Mitarbeiter: ${report.staff_member.name}`, 20, 72);
      doc.text(`Rolle: ${report.staff_member.role}`, 20, 79);
      doc.text(`Filiale: ${report.staff_member.branch}`, 20, 86);
      
      // Financial Performance Table
      const financialData = [
        ['Gesamtumsatz', `${report.financial_performance.total_sales.toFixed(2)} EUR`],
        ['Anzahl Bestellungen', report.financial_performance.total_orders.toString()],
        ['Anzahl Kunden', report.financial_performance.total_customers.toString()],
        ['Durchschnittlicher Bestellwert', `${report.financial_performance.average_order_value.toFixed(2)} EUR`],
        ['Bruttoumsatz', `${report.financial_performance.gross_revenue.toFixed(2)} EUR`],
        ['Nettoumsatz', `${report.financial_performance.net_revenue.toFixed(2)} EUR`],
        ['MwSt. (19%)', `${report.financial_performance.tax_collected.toFixed(2)} EUR`],
        ['Gewährte Rabatte', `${report.financial_performance.discounts_given.toFixed(2)} EUR`],
        ['Erstattungen', `${report.financial_performance.refunds_issued.toFixed(2)} EUR`],
        ['Umsatz pro Stunde', `${report.financial_performance.sales_per_hour.toFixed(2)} EUR`],
        ['Bestellungen pro Stunde', `${report.financial_performance.orders_per_hour.toFixed(1)}`]
      ];
      
      autoTable(doc, {
        startY: 95,
        head: [['FINANZLEISTUNG', '']],
        body: financialData,
        theme: 'grid',
        headStyles: { fillColor: [255, 0, 0], textColor: 255, fontSize: 12, fontStyle: 'bold' },
        styles: { fontSize: 10 }
      });
      
      // Payment Performance Table
      const paymentData = [
        ['Bargeld', `${report.payment_performance.cash.amount.toFixed(2)} EUR`, `${report.payment_performance.cash.percentage.toFixed(1)}%`],
        ['Karte', `${report.payment_performance.card.amount.toFixed(2)} EUR`, `${report.payment_performance.card.percentage.toFixed(1)}%`],
        ['PayPal', `${report.payment_performance.paypal.amount.toFixed(2)} EUR`, `${report.payment_performance.paypal.percentage.toFixed(1)}%`],
        ['Sonstige', `${report.payment_performance.other.amount.toFixed(2)} EUR`, `${report.payment_performance.other.percentage.toFixed(1)}%`]
      ];
      
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['ZAHLUNGSLEISTUNG', 'Betrag', 'Prozent']],
        body: paymentData,
        theme: 'grid',
        headStyles: { fillColor: [255, 0, 0], textColor: 255, fontSize: 12, fontStyle: 'bold' },
        styles: { fontSize: 10 }
      });
      
      // Cash Management Table
      const cashData = [
        ['Kassenbestand Anfang', `${report.cash_management.opening_cash.toFixed(2)} EUR`],
        ['Bargeldumsatz', `${report.cash_management.cash_sales.toFixed(2)} EUR`],
        ['Bargelderstattungen', `${report.cash_management.cash_refunds.toFixed(2)} EUR`],
        ['Erwarteter Kassenbestand', `${report.cash_management.expected_cash.toFixed(2)} EUR`],
        ['Tatsächlicher Kassenbestand', `${report.cash_management.actual_cash.toFixed(2)} EUR`],
        ['Kassendifferenz', `${report.cash_management.cash_difference.toFixed(2)} EUR`],
        ['Kassenüberschuss/-fehlbetrag', `${report.cash_management.cash_over_short.toFixed(2)} EUR`],
        ['Kassengenauigkeit', `${report.cash_management.cash_accuracy_percentage.toFixed(1)}%`]
      ];
      
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['KASSENVERWALTUNG', '']],
        body: cashData,
        theme: 'grid',
        headStyles: { fillColor: [255, 0, 0], textColor: 255, fontSize: 12, fontStyle: 'bold' },
        styles: { fontSize: 10 }
      });
      
      // Team Comparison Table
      const teamData = [
        ['Team-Rang', `#${report.team_comparison.staff_rank}`],
        ['Gesamtmitarbeiter', report.team_comparison.total_staff_count.toString()],
        ['Überdurchschnittliche Leistung', report.team_comparison.above_average_performance ? 'Ja' : 'Nein'],
        ['Team-Durchschnitt Umsatz', `${report.team_comparison.team_average_sales.toFixed(2)} EUR`],
        ['Team-Durchschnitt Bestellungen', report.team_comparison.team_average_orders.toString()],
        ['Leistung vs Team', `${report.team_comparison.performance_vs_team_percentage.toFixed(1)}%`]
      ];
      
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['TEAM-VERGLEICH', '']],
        body: teamData,
        theme: 'grid',
        headStyles: { fillColor: [255, 0, 0], textColor: 255, fontSize: 12, fontStyle: 'bold' },
        styles: { fontSize: 10 }
      });
      
      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.text(`Bericht erstellt: ${new Date(report.audit_trail.report_generated_at).toLocaleString('de-DE')}`, 20, pageHeight - 20);
      doc.text(`Erstellt von: ${report.audit_trail.generated_by}`, 20, pageHeight - 15);
      doc.text('SAHEEH SUSHI & BOWLS - MITARBEITER TAGESABSCHLUSSBERICHT', 105, pageHeight - 10, { align: 'center' });
      
      // Save PDF
      const fileName = `Mitarbeiter_Tagesabschlussbericht_${report.report_date}_${report.staff_member.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);
      
      console.log('✅ [EXPORT] PDF exported successfully');
      
    } catch (error) {
      console.error('❌ [EXPORT] Failed to export PDF:', error);
      alert('Fehler beim Exportieren der PDF-Datei');
    }
  };

  const exportReportToExcel = async (report: AdminDailyClosingReport) => {
    try {
      console.log('📊 [EXPORT] Exporting admin daily closing report to Excel...');
      
      // Import XLSX
      const XLSX = await import('xlsx');
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Summary Sheet
      const summaryData = [
        ['SAHEEH SUSHI & BOWLS - MITARBEITER TAGESABSCHLUSSBERICHT'],
        ['[LOGO WILL BE DISPLAYED]'],
        [''],
        ['Berichtsdaten'],
        ['Datum', new Date(report.report_date).toLocaleDateString('de-DE')],
        ['Mitarbeiter', report.staff_member.name],
        ['Rolle', report.staff_member.role],
        ['Filiale', report.staff_member.branch],
        [''],
        ['FINANZLEISTUNG'],
        ['Gesamtumsatz', `${report.financial_performance.total_sales.toFixed(2)} EUR`],
        ['Anzahl Bestellungen', report.financial_performance.total_orders],
        ['Anzahl Kunden', report.financial_performance.total_customers],
        ['Durchschnittlicher Bestellwert', `${report.financial_performance.average_order_value.toFixed(2)} EUR`],
        ['Bruttoumsatz', `${report.financial_performance.gross_revenue.toFixed(2)} EUR`],
        ['Nettoumsatz', `${report.financial_performance.net_revenue.toFixed(2)} EUR`],
        ['MwSt. (19%)', `${report.financial_performance.tax_collected.toFixed(2)} EUR`],
        ['Gewährte Rabatte', `${report.financial_performance.discounts_given.toFixed(2)} EUR`],
        ['Erstattungen', `${report.financial_performance.refunds_issued.toFixed(2)} EUR`],
        ['Umsatz pro Stunde', `${report.financial_performance.sales_per_hour.toFixed(2)} EUR`],
        ['Bestellungen pro Stunde', report.financial_performance.orders_per_hour]
      ];
      
      const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWS, 'Zusammenfassung');
      
      // Payment Performance Sheet
      const paymentData = [
        ['ZAHLUNGSLEISTUNG'],
        [''],
        ['Methode', 'Betrag (EUR)', 'Prozent'],
        ['Bargeld', report.payment_performance.cash.amount, report.payment_performance.cash.percentage],
        ['Karte', report.payment_performance.card.amount, report.payment_performance.card.percentage],
        ['PayPal', report.payment_performance.paypal.amount, report.payment_performance.paypal.percentage],
        ['Sonstige', report.payment_performance.other.amount, report.payment_performance.other.percentage]
      ];
      
      const paymentWS = XLSX.utils.aoa_to_sheet(paymentData);
      XLSX.utils.book_append_sheet(wb, paymentWS, 'Zahlungsleistung');
      
      // Cash Management Sheet
      const cashData = [
        ['KASSENVERWALTUNG'],
        [''],
        ['Beschreibung', 'Betrag (EUR)'],
        ['Kassenbestand Anfang', report.cash_management.opening_cash],
        ['Bargeldumsatz', report.cash_management.cash_sales],
        ['Bargelderstattungen', report.cash_management.cash_refunds],
        ['Erwarteter Kassenbestand', report.cash_management.expected_cash],
        ['Tatsächlicher Kassenbestand', report.cash_management.actual_cash],
        ['Kassendifferenz', report.cash_management.cash_difference],
        ['Kassenüberschuss/-fehlbetrag', report.cash_management.cash_over_short],
        ['Kassengenauigkeit (%)', report.cash_management.cash_accuracy_percentage]
      ];
      
      const cashWS = XLSX.utils.aoa_to_sheet(cashData);
      XLSX.utils.book_append_sheet(wb, cashWS, 'Kassenverwaltung');
      
      // Team Comparison Sheet
      const teamData = [
        ['TEAM-VERGLEICH'],
        [''],
        ['Beschreibung', 'Wert'],
        ['Team-Rang', report.team_comparison.staff_rank],
        ['Gesamtmitarbeiter', report.team_comparison.total_staff_count],
        ['Überdurchschnittliche Leistung', report.team_comparison.above_average_performance ? 'Ja' : 'Nein'],
        ['Team-Durchschnitt Umsatz', report.team_comparison.team_average_sales],
        ['Team-Durchschnitt Bestellungen', report.team_comparison.team_average_orders],
        ['Leistung vs Team (%)', report.team_comparison.performance_vs_team_percentage]
      ];
      
      const teamWS = XLSX.utils.aoa_to_sheet(teamData);
      XLSX.utils.book_append_sheet(wb, teamWS, 'Team-Vergleich');
      
      // Save Excel file
      const fileName = `Mitarbeiter_Tagesabschlussbericht_${report.report_date}_${report.staff_member.name.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ [EXPORT] Excel exported successfully');
      
    } catch (error) {
      console.error('❌ [EXPORT] Failed to export Excel:', error);
      alert('Fehler beim Exportieren der Excel-Datei');
    }
  };

  const getPerformanceTrendIcon = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return <TrendingUp color="success" />;
      case 'declining':
        return <TrendingDown color="error" />;
      default:
        return <TrendingFlat color="info" />;
    }
  };

  const getPerformanceTrendColor = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return 'success';
      case 'declining':
        return 'error';
      default:
        return 'info';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: { xs: 2, md: 3 }, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {t('dailyClosing.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Assessment />}
              onClick={() => setGenerateReportDialog(true)}
              sx={{ backgroundColor: theme.palette.primary.main }}
            >
              {t('dailyClosing.generateReport')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
            >
              {t('common.refresh')}
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin reports tabs">
            <Tab label={t('dailyClosing.staffPerformanceOverview')} icon={<People />} />
            <Tab label={t('dailyClosing.dailyClosingReports')} icon={<Assessment />} />
            <Tab label={t('dailyClosing.interimSalesSummaries')} icon={<TrendingUp />} />
            <Tab label={t('dailyClosing.staffAnalytics')} icon={<BarChartIcon />} />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Staff Performance Overview */}
          <Grid container spacing={3}>
            {staffPerformance.map((staff, index) => (
              <Grid item xs={12} md={6} lg={4} key={staff.staff_id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: theme.palette.primary.main }}>
                        {staff.staff_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" component="div">
                          {staff.staff_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {staff.staff_role} • {staff.staff_email}
                        </Typography>
                      </Box>
                      <Chip
                        label={`#${index + 1}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dailyClosing.avgDailySales')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          €{staff.average_daily_sales.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dailyClosing.avgDailyOrders')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {staff.average_daily_orders.toFixed(1)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dailyClosing.consistencyScore')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LinearProgress
                            variant="determinate"
                            value={staff.consistency_score}
                            sx={{ flex: 1, mr: 1 }}
                          />
                          <Typography variant="body2">
                            {staff.consistency_score.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dailyClosing.performanceTrend')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getPerformanceTrendIcon(staff.improvement_trend)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {staff.improvement_trend}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Last Report: {new Date(staff.last_report_date).toLocaleDateString()}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => {
                          setSelectedStaff(staff);
                          setTabValue(1); // Switch to Daily Closing Reports tab
                        }}
                      >
                        {t('dailyClosing.viewReports')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Daily Closing Reports */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
                <Typography variant="h6">
                  {t('dailyClosing.staffDailyClosingReports')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label={t('dailyClosing.filterByDate')}
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('dailyClosing.filterByStaff')}</InputLabel>
                    <Select
                      value={staffFilter}
                      onChange={(e) => setStaffFilter(e.target.value)}
                      label={t('dailyClosing.filterByStaff')}
                    >
                      <MenuItem value="">{t('dailyClosing.allStaff')}</MenuItem>
                      {staffPerformance.map((staff) => (
                        <MenuItem key={staff.staff_id} value={staff.staff_id}>
                          {staff.staff_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              
              {dailyReports.length === 0 ? (
                <Alert severity="info">
                  {t('dailyClosing.noReportsFound')}
                </Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.date')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.staffMember')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.totalSales')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.orders')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.cashAccuracy')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.teamRank')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.status')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyReports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            {new Date(report.report_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                                {report.staff_member.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {report.staff_member.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {report.staff_member.role}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              €{report.financial_performance.total_sales.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{report.financial_performance.total_orders}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Chip
                              label={`${report.cash_management.cash_accuracy_percentage.toFixed(1)}%`}
                              color={report.cash_management.cash_accuracy_percentage > 95 ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Chip
                              label={`#${report.team_comparison.staff_rank}`}
                              color={report.team_comparison.staff_rank <= 3 ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Chip
                              label={report.status}
                              color={report.status === 'final' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Tooltip title={t('dailyClosing.viewDetails')}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedReport(report);
                                    setViewReportDialog(true);
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('dailyClosing.exportPDF')}>
                                <IconButton
                                  size="small"
                                  onClick={() => exportReportToPDF(report)}
                                >
                                  <PictureAsPdf />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('dailyClosing.exportExcel')}>
                                <IconButton
                                  size="small"
                                  onClick={() => exportReportToExcel(report)}
                                >
                                  <InsertDriveFile />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Reset History */}
          <Box sx={{ mt: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🔁 Reset-Historie der Tagesabschlüsse
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Zeigt alle zurückgesetzten Tagesabschlussberichte mit Grund und verantwortlichem Mitarbeiter.
                </Typography>
                {resetEvents.length === 0 ? (
                  <Alert severity="info">
                    Bisher wurden keine Tagesabschlussberichte zurückgesetzt.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Reset-Zeit</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Berichtsdatum</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Kassierer</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Filiale</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Vorheriger Status</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Zurückgesetzt von</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>Grund</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resetEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                              {new Date(event.created_at).toLocaleString('de-DE')}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                              {event.report_date
                                ? new Date(event.report_date).toLocaleDateString('de-DE')
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{event.cashier_name || '-'}</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{event.branch || '-'}</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{event.previous_status || '-'}</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {event.performed_by_name}
                                </Typography>
                                {event.performed_by_email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {event.performed_by_email}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" noWrap title={event.reason}>
                                {event.reason}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Interim Sales Summaries */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
                <Typography variant="h6">
                  {t('dailyClosing.realtimeMonitoring')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadData}
                >
                  {t('common.refresh')}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('dailyClosing.interimDescription')}
              </Typography>
              
              {interimSummaries.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('dailyClosing.noInterimSummaries')}
                </Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.time')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.staffMember')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.currentSales')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.currentOrders')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.hourlyRate')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.projectedDaily')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.trend')}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{t('dailyClosing.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interimSummaries.map((summary) => {
                        const staffName = summary.staff_member?.name || 'Unknown';
                        const staffRole = summary.staff_member?.role || 'staff';
                        const currentSales = summary.current_performance?.current_sales ?? 0;
                        const currentOrders = summary.current_performance?.current_orders ?? 0;
                        const hourlyRate = summary.current_performance?.hourly_rate ?? 0;
                        const projectedDaily = summary.current_performance?.projected_daily_sales ?? 0;
                        const trend = summary.recent_activity?.current_trend || 'stable';
                        return (
                        <TableRow key={summary.id}>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            {new Date(summary.summary_time).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                                {staffName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {staffName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {staffRole}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              €{currentSales.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>{currentOrders}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            €{hourlyRate.toFixed(2)}/hr
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            €{projectedDaily.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Chip
                              label={trend}
                              color={
                                trend === 'increasing' ? 'success' :
                                trend === 'decreasing' ? 'error' : 'default'
                              }
                              size="small"
                              icon={
                                trend === 'increasing' ? <TrendingUp /> :
                                trend === 'decreasing' ? <TrendingDown /> : <TrendingFlat />
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                            <Tooltip title={t('dailyClosing.viewDetails')}>
                              <IconButton size="small">
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          
          {/* Generate New Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dailyClosing.generateNewInterimSummary')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('dailyClosing.generateInterimDescription')}
              </Typography>
              
              <Grid container spacing={2}>
                {staffPerformance.map((staff) => (
                  <Grid item xs={12} md={6} lg={4} key={staff.staff_id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ mr: 2, bgcolor: theme.palette.secondary.main }}>
                            {staff.staff_name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">
                              {staff.staff_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {staff.staff_role}
                            </Typography>
                          </Box>
                          <Chip
                            label={staff.status}
                            color={staff.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                        
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<TrendingUp />}
                          onClick={() => generateInterimSummary(staff.staff_id)}
                          sx={{ backgroundColor: theme.palette.success.main }}
                        >
                          {t('dailyClosing.generateInterimSummary')}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Staff Analytics */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('dailyClosing.staffPerformanceComparison')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={staffPerformance.slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="staff_name" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => [`€${value}`, 'Average Daily Sales']} />
                      <Bar dataKey="average_daily_sales" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('dailyClosing.performanceTrends')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Improving', value: staffPerformance.filter(s => s.improvement_trend === 'improving').length },
                          { name: 'Stable', value: staffPerformance.filter(s => s.improvement_trend === 'stable').length },
                          { name: 'Declining', value: staffPerformance.filter(s => s.improvement_trend === 'declining').length },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#4caf50" />
                        <Cell fill="#2196f3" />
                        <Cell fill="#f44336" />
                      </Pie>
                      <Legend />
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Generate Report Dialog */}
        <Dialog
          open={generateReportDialog}
          onClose={() => setGenerateReportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t('dailyClosing.generateStaffReport')}
            <IconButton
              onClick={() => setGenerateReportDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('dailyClosing.selectStaffMember')}</InputLabel>
                <Select
                  value={reportForm.staffId}
                  onChange={(e) => setReportForm(prev => ({ ...prev, staffId: e.target.value }))}
                  label={t('dailyClosing.selectStaffMember')}
                >
                  {staffPerformance.map((staff) => (
                    <MenuItem key={staff.staff_id} value={staff.staff_id}>
                      {staff.staff_name} ({staff.staff_role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('dailyClosing.reportDate')}
                type="date"
                value={reportForm.reportDate}
                onChange={(e) => setReportForm(prev => ({ ...prev, reportDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={t('dailyClosing.openingCash')}
                type="number"
                value={reportForm.openingCash}
                onChange={(e) => setReportForm(prev => ({ ...prev, openingCash: parseFloat(e.target.value) || 0 }))}
                fullWidth
              />
              <TextField
                label={t('dailyClosing.actualCash')}
                type="number"
                value={reportForm.actualCash}
                onChange={(e) => setReportForm(prev => ({ ...prev, actualCash: parseFloat(e.target.value) || 0 }))}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateReportDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={generateStaffReport}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : t('dailyClosing.generateReport')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog
          open={viewReportDialog}
          onClose={() => setViewReportDialog(false)}
          maxWidth="lg" fullWidth
        >
          <DialogTitle>
            {t('dailyClosing.reportDetails')}
            <IconButton
              onClick={() => setViewReportDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <Box sx={{ marginTop: 2 }}>
                {/* Staff Information */}
                <Card sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('dailyClosing.staffInformation')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.staffName')}</Typography>
                        <Typography variant="h6">{selectedReport.staff_member.name}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.role')}</Typography>
                        <Typography variant="h6">{selectedReport.staff_member.role}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.branch')}</Typography>
                        <Typography variant="h6">{selectedReport.staff_member.branch}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.shiftDuration')}</Typography>
                        <Typography variant="h6">{selectedReport.shift_info.duration_hours.toFixed(1)}h</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Financial Performance */}
                <Card sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('dailyClosing.financialPerformance')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.totalSales')}</Typography>
                        <Typography variant="h6">€{selectedReport.financial_performance.total_sales.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.totalOrders')}</Typography>
                        <Typography variant="h6">{selectedReport.financial_performance.total_orders}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.salesPerHour')}</Typography>
                        <Typography variant="h6">€{selectedReport.financial_performance.sales_per_hour.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.ordersPerHour')}</Typography>
                        <Typography variant="h6">{selectedReport.financial_performance.orders_per_hour.toFixed(1)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Team Comparison */}
                <Card sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('dailyClosing.teamComparison')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.teamRank')}</Typography>
                        <Typography variant="h6">#{selectedReport.team_comparison.staff_rank}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.totalStaff')}</Typography>
                        <Typography variant="h6">{selectedReport.team_comparison.total_staff_count}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.vsTeamAverage')}</Typography>
                        <Typography 
                          variant="h6" 
                          color={selectedReport.team_comparison.performance_vs_team_percentage > 0 ? 'success.main' : 'error.main'}
                        >
                          {selectedReport.team_comparison.performance_vs_team_percentage > 0 ? '+' : ''}
                          {selectedReport.team_comparison.performance_vs_team_percentage.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.performance')}</Typography>
                        <Chip
                          label={selectedReport.team_comparison.above_average_performance ? 'Above Average' : 'Below Average'}
                          color={selectedReport.team_comparison.above_average_performance ? 'success' : 'warning'}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Performance Metrics */}
                <Card sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('dailyClosing.performanceMetrics')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.peakHour')}</Typography>
                        <Typography variant="h6">{selectedReport.performance_metrics.peak_performance_hour}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.efficiencyScore')}</Typography>
                        <Typography variant="h6">{selectedReport.performance_metrics.efficiency_score.toFixed(0)}%</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.errorRate')}</Typography>
                        <Typography variant="h6">{selectedReport.performance_metrics.error_rate_percentage.toFixed(1)}%</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">{t('dailyClosing.serviceTime')}</Typography>
                        <Typography variant="h6">{selectedReport.performance_metrics.average_service_time_minutes}min</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Hourly Sales Chart */}
                <Card sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('dailyClosing.hourlySalesBreakdown')}
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={selectedReport.sales_analysis.hourly_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <RechartsTooltip formatter={(value) => [`€${value}`, 'Revenue']} />
                        <Bar dataKey="revenue" fill={theme.palette.primary.main} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewReportDialog(false)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => selectedReport && exportReportToPDF(selectedReport)}
              variant="contained"
              startIcon={<PictureAsPdf />}
            >
              {t('dailyClosing.exportPDF')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AdminDailyClosingReportsPage;
